import { apiClient } from '@api/client';
import type {
  AttachIncidentCustomersResult,
  Incident,
  IncidentCustomer,
  IncidentPayload,
  IncidentStatus,
  IncidentUpdatePayload,
} from '@models/incidents';

export type PageMeta = {
  page: number;
  limit: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
};

export type SortOrder = 'asc' | 'desc';

export type GetIncidentsParams = {
  page: number;
  limit: number;
  statuses?: IncidentStatus[];
  search?: string;
  sortOrder?: SortOrder;
};

export type IncidentsResponse = {
  data: Incident[];
  meta: PageMeta;
};

export type GetIncidentCustomersParams = {
  page: number;
  limit: number;
  sortOrder?: SortOrder;
};

export type IncidentCustomersResponse = {
  data: IncidentCustomer[];
  meta: PageMeta;
};

export type AttachIncidentCustomersParams = {
  incidentId: string;
  customerIds: string[];
  note?: string;
};

export type DetachIncidentCustomerParams = {
  incidentId: string;
  customerId: string;
};

export async function getIncidents(params: GetIncidentsParams): Promise<IncidentsResponse> {
  // The API takes the status list as one comma-separated query value.
  const { statuses, ...rest } = params;
  const { data } = await apiClient.get<IncidentsResponse>('/incidents', {
    params: { ...rest, statuses: statuses && statuses.length > 0 ? statuses.join(',') : undefined },
  });
  return data;
}

export async function getIncident(id: string): Promise<Incident> {
  const { data } = await apiClient.get<Incident>(`/incidents/${encodeURIComponent(id)}`);
  return data;
}

export async function createIncident(payload: IncidentPayload): Promise<Incident> {
  const { data } = await apiClient.post<Incident>('/incidents', payload);
  return data;
}

export async function updateIncident(
  id: string,
  payload: IncidentUpdatePayload,
): Promise<Incident> {
  const { data } = await apiClient.patch<Incident>(`/incidents/${encodeURIComponent(id)}`, payload);
  return data;
}

export async function getIncidentCustomers(
  incidentId: string,
  params: GetIncidentCustomersParams,
): Promise<IncidentCustomersResponse> {
  const { data } = await apiClient.get<IncidentCustomersResponse>(
    `/incidents/${encodeURIComponent(incidentId)}/customers`,
    { params },
  );
  return data;
}

// Idempotent on the server: ids already attached are counted in alreadyAttachedCount, not re-written.
export async function attachIncidentCustomers({
  incidentId,
  customerIds,
  note,
}: AttachIncidentCustomersParams): Promise<AttachIncidentCustomersResult> {
  const { data } = await apiClient.post<AttachIncidentCustomersResult>(
    `/incidents/${encodeURIComponent(incidentId)}/customers`,
    { customerIds, note: note || undefined },
  );
  return data;
}

export async function detachIncidentCustomer({
  incidentId,
  customerId,
}: DetachIncidentCustomerParams): Promise<void> {
  await apiClient.delete(
    `/incidents/${encodeURIComponent(incidentId)}/customers/${encodeURIComponent(customerId)}`,
  );
}
